// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/renderer/ui/ai-speech-io.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * The AI tab's mic and voice (docs/AI_CHATBOT_PLAN.md, phase 5), loaded with the
 * tab. The mic records the question and hands main a 16 kHz mono WAV (the one
 * format every speech endpoint reads); the voice speaks a reply sentence by
 * sentence, making the next piece while the current one plays.
 *
 * Voice mode (docs/AI_CHATBOT_VOICE.md): the recorder listens for speech and
 * stops itself after a pause, so a question needs one tap, and a recording
 * with no speech in it is never sent (speech-to-text models invent words from
 * silence). A reply plays from the speech endpoint (a WAV from the local
 * speech server, an MP3 from the AYNI gateway) or, for a language that
 * endpoint does not read or when there is none, from the device's own voice.
 */
import { splitForSpeech } from '../../shared/ai-speakable-text';
import type { AiChatLanguage } from '../../shared/ai-languages';

const TARGET_RATE = 16_000;
/** A question longer than this is sent as it is. */
export const MAX_RECORDING_MS = 30_000;

/** Browsers allow the mic only on secure pages (https, or this computer). */
export function speechInputSupported(): boolean {
  return typeof window !== 'undefined'
    && window.isSecureContext
    && typeof navigator !== 'undefined'
    && typeof navigator.mediaDevices?.getUserMedia === 'function'
    && typeof MediaRecorder !== 'undefined';
}

/** Mono samples at any rate → 16 kHz 16-bit PCM WAV. */
export function encodeWav16kMono(samples: Float32Array, sampleRate: number): Uint8Array {
  const ratio = sampleRate / TARGET_RATE;
  const length = Math.max(0, Math.floor(samples.length / ratio));
  const pcm = new Int16Array(length);
  for (let index = 0; index < length; index++) {
    // Average the source samples this output sample covers (a simple low-pass).
    const from = Math.floor(index * ratio);
    const to = Math.max(from + 1, Math.floor((index + 1) * ratio));
    let sum = 0;
    for (let at = from; at < to && at < samples.length; at++) sum += samples[at]!;
    const value = Math.max(-1, Math.min(1, sum / (to - from)));
    pcm[index] = value < 0 ? value * 0x8000 : value * 0x7fff;
  }
  const bytes = new Uint8Array(44 + pcm.byteLength);
  const view = new DataView(bytes.buffer);
  const text = (offset: number, value: string) => { for (let i = 0; i < value.length; i++) bytes[offset + i] = value.charCodeAt(i); };
  text(0, 'RIFF');
  view.setUint32(4, 36 + pcm.byteLength, true);
  text(8, 'WAVE');
  text(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, TARGET_RATE, true);
  view.setUint32(28, TARGET_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  text(36, 'data');
  view.setUint32(40, pcm.byteLength, true);
  bytes.set(new Uint8Array(pcm.buffer), 44);
  return bytes;
}

/**
 * When the recorder decides a question is over. All in milliseconds; the
 * loudness is the RMS of the mic's samples (0..1).
 */
export interface SpeechDetection {
  /** A pause this long after speech ends the question. */
  endSilenceMs: number;
  /** With no speech by then, the recording ends empty. */
  noSpeechMs: number;
  /** Speech must last this long to count (a cough or a click does not). */
  minSpeechMs: number;
}

export const DEFAULT_SPEECH_DETECTION: SpeechDetection = { endSilenceMs: 1200, noSpeechMs: 7000, minSpeechMs: 250 };

/**
 * Hears speech in a stream of loudness readings, one every `stepMs`: the
 * first readings set the room's noise floor, then loud stretches count as
 * speech. Pure, so it is tested without a microphone.
 */
export class SpeechActivity {
  private elapsed = 0;
  private floor = 0;
  private floorReadings = 0;
  private loudFor = 0;
  private quietFor = 0;
  private heard = false;
  private finished: 'speech-end' | 'no-speech' | null = null;

  constructor(private readonly detection: SpeechDetection = DEFAULT_SPEECH_DETECTION, private readonly stepMs = 50) {}

  /** Whether real speech was heard. */
  public get heardSpeech(): boolean {
    return this.heard;
  }

  /** Takes one reading; says when the question is over, and why. */
  public push(rms: number): 'speech-end' | 'no-speech' | null {
    if (this.finished) return null;
    this.elapsed += this.stepMs;
    // The first 300 ms are the room's own noise.
    if (this.elapsed <= 300) {
      this.floor = (this.floor * this.floorReadings + rms) / ++this.floorReadings;
      return null;
    }
    const threshold = Math.max(0.012, this.floor * 2.5);
    if (rms > threshold) {
      this.loudFor += this.stepMs;
      this.quietFor = 0;
      if (this.loudFor >= this.detection.minSpeechMs) this.heard = true;
    } else {
      this.quietFor += this.stepMs;
      if (this.quietFor >= 200) this.loudFor = 0;
    }
    if (this.heard && this.quietFor >= this.detection.endSilenceMs) this.finished = 'speech-end';
    else if (!this.heard && this.elapsed >= this.detection.noSpeechMs) this.finished = 'no-speech';
    return this.finished;
  }
}

export interface RecordingOptions {
  /** The question ran past MAX_RECORDING_MS. */
  onLimit: () => void;
  /** The visitor stopped speaking, or never started; absent: the recorder only stops when told. */
  onEnd?: (reason: 'speech-end' | 'no-speech') => void;
  detection?: SpeechDetection;
}

/** Records one question from the mic. */
export class SpeechRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private meter: { context: AudioContext; poll: ReturnType<typeof setInterval> } | null = null;
  private activity: SpeechActivity | null = null;

  /**
   * Rejects when the mic is refused or missing. Given a function, it is
   * `onLimit` alone (the mic stops when tapped again); given options, the
   * recorder also hears when the question is over.
   */
  public async start(options: RecordingOptions | (() => void)): Promise<void> {
    const { onLimit, onEnd, detection } = typeof options === 'function' ? { onLimit: options } as RecordingOptions : options;
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
    this.chunks = [];
    this.recorder = new MediaRecorder(this.stream);
    this.recorder.ondataavailable = (event) => { if (event.data.size) this.chunks.push(event.data); };
    this.recorder.start();
    this.timer = setTimeout(onLimit, MAX_RECORDING_MS);
    this.activity = null;
    if (onEnd) this.listen(onEnd, detection ?? DEFAULT_SPEECH_DETECTION);
  }

  /** Measures the mic's loudness while it records, to hear when the question is over. */
  private listen(onEnd: (reason: 'speech-end' | 'no-speech') => void, detection: SpeechDetection): void {
    if (!this.stream || typeof AudioContext === 'undefined') return;
    try {
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      context.createMediaStreamSource(this.stream).connect(analyser);
      const samples = new Float32Array(analyser.fftSize);
      const activity = new SpeechActivity(detection, 50);
      this.activity = activity;
      const poll = setInterval(() => {
        analyser.getFloatTimeDomainData(samples);
        let sum = 0;
        for (const sample of samples) sum += sample * sample;
        const ended = activity.push(Math.sqrt(sum / samples.length));
        if (ended) onEnd(ended);
      }, 50);
      this.meter = { context, poll };
    } catch {
      // Without a meter the mic still works; it stops when tapped.
      this.activity = null;
    }
  }

  public get recording(): boolean {
    return this.recorder?.state === 'recording';
  }

  /** False only when the recorder listened and heard no speech (then nothing is worth sending). */
  public get heardSpeech(): boolean {
    return this.activity ? this.activity.heardSpeech : true;
  }

  /** The question as a WAV, or null if nothing was said. */
  public async stop(): Promise<Uint8Array | null> {
    const recorder = this.recorder;
    if (!recorder) return null;
    await new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
      if (recorder.state !== 'inactive') recorder.stop();
      else resolve();
    });
    const heard = this.heardSpeech;
    this.release();
    const blob = new Blob(this.chunks, { type: recorder.mimeType });
    if (!blob.size || !heard) return null;
    const context = new AudioContext();
    try {
      const audio = await context.decodeAudioData(await blob.arrayBuffer());
      const mono = new Float32Array(audio.length);
      for (let channel = 0; channel < audio.numberOfChannels; channel++) {
        const data = audio.getChannelData(channel);
        for (let index = 0; index < data.length; index++) mono[index]! += data[index]! / audio.numberOfChannels;
      }
      return encodeWav16kMono(mono, audio.sampleRate);
    } finally {
      void context.close();
    }
  }

  public cancel(): void {
    if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    this.release();
  }

  private release(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    if (this.meter) {
      clearInterval(this.meter.poll);
      void this.meter.context.close();
      this.meter = null;
    }
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.recorder = null;
  }
}

/**
 * Phrases speech-to-text models write when they hear noise rather than a
 * question (subtitle credits from their training data). Such a text is
 * dropped, not sent.
 */
const PHANTOM_TRANSCRIPTS: ReadonlyArray<RegExp> = [
  /thanks? (you )?for watching/,
  /please subscribe|like and subscribe/,
  /subtitles? (by|from)|captions? by|amara\.org/,
  /gracias por ver|suscr[ií]bete|subt[ií]tulos (por|realizados)/,
  /bedankt voor het kijken|ondertiteling|ondertiteld door/,
  /gr[àa]cies per (veure|mirar)|subt[íi]tols/
];

/** Whether a transcript is empty or only what a model writes for noise. */
export function isPhantomTranscript(text: string): boolean {
  const plain = text.toLowerCase().replace(/[^\p{L}\p{N}. ]+/gu, ' ').replace(/\s+/g, ' ').trim();
  if (!/[\p{L}\p{N}]/u.test(plain)) return true;
  return PHANTOM_TRANSCRIPTS.some((pattern) => pattern.test(plain));
}

/** The audio's type, from its first bytes: a WAV from a speech server, an MP3 from the AYNI gateway. */
export function audioMimeType(audio: Uint8Array): string {
  const head = String.fromCharCode(...audio.subarray(0, 4));
  if (head === 'RIFF') return 'audio/wav';
  if (head.startsWith('ID3') || (audio[0] === 0xff && ((audio[1] ?? 0) & 0xe0) === 0xe0)) return 'audio/mpeg';
  return 'application/octet-stream';
}

const DEVICE_LANGUAGE: Record<AiChatLanguage, string> = { en: 'en', es: 'es', nl: 'nl', ca: 'ca' };

/** The device's own voices (Windows, macOS, Android, the Quest browser), when it has one for the language. */
export class DeviceVoice {
  private utterance: SpeechSynthesisUtterance | null = null;

  private get synth(): SpeechSynthesis | null {
    return typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null;
  }

  private voiceFor(language: AiChatLanguage): SpeechSynthesisVoice | null {
    const voices = this.synth?.getVoices() ?? [];
    const prefix = DEVICE_LANGUAGE[language];
    return voices.find((voice) => voice.lang.toLowerCase().startsWith(prefix) && voice.localService)
      ?? voices.find((voice) => voice.lang.toLowerCase().startsWith(prefix))
      ?? null;
  }

  public has(language: AiChatLanguage): boolean {
    return this.voiceFor(language) !== null;
  }

  /** Says the text; resolves when it has been said, or at once when there is no voice. */
  public say(text: string, language: AiChatLanguage): Promise<void> {
    const synth = this.synth;
    const voice = this.voiceFor(language);
    if (!synth || !voice) return Promise.resolve();
    return new Promise((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.voice = voice;
      utterance.lang = voice.lang;
      utterance.onend = () => resolve();
      utterance.onerror = () => resolve();
      this.utterance = utterance;
      synth.speak(utterance);
    });
  }

  public stop(): void {
    if (this.utterance) this.synth?.cancel();
    this.utterance = null;
  }
}

export type SpeakPiece = (text: string, language: AiChatLanguage) => Promise<{ ok: true; audio: Uint8Array } | { ok: false; code: string; message: string }>;

/** Failures after which the device's own voice reads instead: no voice there, or not for this language. */
const FALL_BACK_TO_DEVICE = new Set(['AI_UNAVAILABLE', 'AI_NOT_CONFIGURED', 'AI_INVALID_REQUEST', 'AI_DISABLED']);

/**
 * Speaks replies: one piece per sentence, the next made while the current plays.
 * A new reply, or stop(), cuts the current one short. When the speech endpoint
 * cannot read a language, the device's voice reads it, and keeps reading that
 * language for the rest of the session.
 */
export class SpeechPlayer {
  private generation = 0;
  private audio: HTMLAudioElement | null = null;
  private readonly deviceLanguages = new Set<AiChatLanguage>();

  constructor(
    private readonly speakPiece: SpeakPiece | null,
    private readonly onError: (code: string) => void,
    private readonly device: DeviceVoice | null = null
  ) {}

  /** Whether any voice can read this language. */
  public canSpeak(language: AiChatLanguage): boolean {
    return Boolean(this.speakPiece) || Boolean(this.device?.has(language));
  }

  /** Reads the reply; resolves once it has been read, cut short or failed. */
  public async say(text: string, language: AiChatLanguage): Promise<void> {
    this.stop();
    const generation = ++this.generation;
    const pieces = splitForSpeech(text, 200);
    if (!pieces.length) return;
    if (!this.speakPiece || this.deviceLanguages.has(language)) {
      await this.sayOnDevice(pieces, language, generation);
      return;
    }
    let next: ReturnType<SpeakPiece> | null = this.speakPiece(pieces[0]!, language);
    for (let index = 0; index < pieces.length && next; index++) {
      const result = await next;
      if (generation !== this.generation) return;
      if (!result.ok) {
        if (FALL_BACK_TO_DEVICE.has(result.code) && this.device?.has(language)) {
          this.deviceLanguages.add(language);
          await this.sayOnDevice(pieces.slice(index), language, generation);
        } else {
          this.onError(result.code);
        }
        return;
      }
      next = index + 1 < pieces.length ? this.speakPiece(pieces[index + 1]!, language) : null;
      await this.play(result.audio, generation);
      if (generation !== this.generation) return;
    }
  }

  private async sayOnDevice(pieces: string[], language: AiChatLanguage, generation: number): Promise<void> {
    if (!this.device?.has(language)) {
      this.onError('AI_NOT_CONFIGURED');
      return;
    }
    for (const piece of pieces) {
      if (generation !== this.generation) return;
      await this.device.say(piece, language);
    }
  }

  public stop(): void {
    this.generation++;
    this.device?.stop();
    if (this.audio) {
      this.audio.pause();
      URL.revokeObjectURL(this.audio.src);
      this.audio = null;
    }
  }

  private play(clip: Uint8Array, generation: number): Promise<void> {
    return new Promise((resolve) => {
      if (generation !== this.generation) {
        resolve();
        return;
      }
      const audio = new Audio(URL.createObjectURL(new Blob([new Uint8Array(clip)], { type: audioMimeType(clip) })));
      this.audio = audio;
      const done = () => {
        URL.revokeObjectURL(audio.src);
        if (this.audio === audio) this.audio = null;
        resolve();
      };
      audio.onended = done;
      audio.onerror = done;
      audio.play().catch(done);
    });
  }
}
